import * as React from "react"
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// --- Slot utilities (replaces @radix-ui/react-slot) ---

type AnyProps = Record<string, unknown>

function composeEventHandlers(
  a?: (...args: unknown[]) => void,
  b?: (...args: unknown[]) => void
) {
  return a && b
    ? (...args: unknown[]) => {
        a(...args)
        b(...args)
      }
    : a || b
}

export function composeRefs<T>(
  ...refs: (React.Ref<T> | undefined)[]
): React.RefCallback<T> {
  return (node) => {
    for (const ref of refs) {
      if (typeof ref === "function") {
        ref(node)
      } else if (ref && typeof ref === "object") {
        ;(ref as React.MutableRefObject<T | null>).current = node
      }
    }
  }
}

function mergeSlotProps(slotProps: AnyProps, childProps: AnyProps): AnyProps {
  const merged: AnyProps = { ...slotProps }

  for (const key of Object.keys(childProps)) {
    const slotVal = slotProps[key]
    const childVal = childProps[key]

    if (key === "className") {
      merged[key] = cn(slotVal as string, childVal as string)
    } else if (key === "style") {
      merged[key] = { ...(slotVal as object), ...(childVal as object) }
    } else if (key === "ref") {
      merged[key] = composeRefs(
        slotVal as React.Ref<unknown>,
        childVal as React.Ref<unknown>
      )
    } else if (typeof slotVal === "function" && typeof childVal === "function") {
      merged[key] = composeEventHandlers(
        slotVal as (...args: unknown[]) => void,
        childVal as (...args: unknown[]) => void
      )
    } else if (key in childProps) {
      merged[key] = childVal
    }
  }

  return merged
}

export const Slot = React.forwardRef<
  HTMLElement,
  React.PropsWithChildren<AnyProps>
>(({ children, ...slotProps }, ref) => {
  if (!React.isValidElement(children)) {
    return null
  }

  const childProps = (children as React.ReactElement<AnyProps>).props
  const merged = mergeSlotProps(slotProps, childProps)
  merged.ref = composeRefs(ref, (childProps as { ref?: React.Ref<unknown> }).ref)

  return React.cloneElement(children as React.ReactElement<AnyProps>, merged)
})
Slot.displayName = "Slot"
